import { useEffect, useState } from "react";
import styles from "./my.module.css";
import placeholderImg from "../../images/photo-placeholder.svg";
import {
  useGetIncomingQuery,
  useGetOutgoingQuery,
  useGetMatchesQuery,
  useGetMatchUnreadCountsQuery,
  useMarkMatchViewedMutation,
  useAcceptRequestMutation,
  useRejectRequestMutation,
  useCancelRequestMutation,
} from "../../services/store/api/matchApi";
import { TProfile, TMatchItem, TMatchRequest } from "../../types";
import Loader from "../../components/loader/loader";
import ProfilePreviewModal from "../../components/profile-preview-modal/profile-preview-modal";
import Heading from "../../components/heading/heading";
import ConfirmModal from "../../components/confirm-modal/confirm-modal";

const tabs = [
  { key: "incoming", label: "Входящие" },
  { key: "matches", label: "Мэтчи" },
  { key: "outgoing", label: "Исходящие" },
] as const;

type TabKey = typeof tabs[number]["key"];

type MatchProfile = TProfile & {
  approved_photos?: TProfile["photos"];
};

type ConfirmAction = {
  id: number;
  type: "reject" | "cancel";
  title: string;
  description: string;
  confirmText: string;
} | null;

const CrossIcon = () => (
  <svg width="18" height="18" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M1.5 20.5L20.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M1.5 1.5L20.5 20.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const getProfilePhotoList = (profile?: MatchProfile | null) => {
  return profile?.photos ?? profile?.approved_photos ?? [];
};

const getPhoto = (profile?: MatchProfile | null) => {
  if (!profile) return placeholderImg;
  const photo = getProfilePhotoList(profile)[0];
  if (!photo) return placeholderImg;
  const url = photo.image_urls?.middle || photo.url;
  return url || placeholderImg;
};

const Card = ({
  profile,
  children,
  onInfoClick,
}: {
  profile?: TProfile;
  children?: React.ReactNode;
  onInfoClick?: (profile: TProfile) => void;
}) => {
  const photoUrl = getPhoto(profile);
  const age = profile?.age;
  return (
    <div className={styles.card}>
      <div className={styles.photo}>
        <img
          src={photoUrl}
          alt={profile?.name ? `Фото ${profile.name}` : ""}
          className={styles.photoImage}
          loading="lazy"
          decoding="async"
        />
        {profile && (
          <button
            type="button"
            className={styles.infoButton}
            onClick={() => onInfoClick?.(profile)}
            aria-label={`Показать анкету ${profile.name}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
            </svg>
          </button>
        )}
        <div className={styles.overlay}>
          <div className={styles.profileLine}>
            <div className={styles.name}>{profile?.name}</div>
            {age !== undefined && <div className={styles.age}>{age}</div>}
          </div>
          {children && <div className={styles.actions}>{children}</div>}
        </div>
      </div>
    </div>
  );
};

export function MyPage() {
  const [tab, setTab] = useState<TabKey>("incoming");
  const [selectedProfile, setSelectedProfile] = useState<TProfile | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const { data: incoming, isLoading: loadingIncoming, refetch: refetchIncoming } = useGetIncomingQuery();
  const { data: outgoing, isLoading: loadingOutgoing, refetch: refetchOutgoing } = useGetOutgoingQuery();
  const { data: matches, isLoading: loadingMatches, refetch: refetchMatches } = useGetMatchesQuery();
  const { data: unreadCounts, refetch: refetchUnreadCounts } = useGetMatchUnreadCountsQuery();

  const [accept, { isLoading: isAccepting }] = useAcceptRequestMutation();
  const [reject, { isLoading: isRejecting }] = useRejectRequestMutation();
  const [cancel, { isLoading: isCanceling }] = useCancelRequestMutation();
  const [markViewed] = useMarkMatchViewedMutation();

  const isBusy = isAccepting || isRejecting || isCanceling;

  const refreshMyTabs = () => {
    refetchIncoming();
    refetchOutgoing();
    refetchMatches();
    refetchUnreadCounts();
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (tab === "incoming" && !loadingIncoming && (unreadCounts?.incoming ?? 0) > 0) {
      timeoutId = setTimeout(() => {
        markViewed("incoming");
      }, 1500);
    }

    if (tab === "matches" && !loadingMatches && (unreadCounts?.matches ?? 0) > 0) {
      timeoutId = setTimeout(() => {
        markViewed("matches");
      }, 1500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loadingIncoming, loadingMatches, markViewed, tab, unreadCounts?.incoming, unreadCounts?.matches]);

  const handleAccept = async (id: number) => {
    try {
      await accept(id).unwrap();
      refreshMyTabs();
    } catch {
      // RTK Query сохранит ошибку запроса, здесь важно не ломать UI.
    }
  };

  const handleTabClick = (nextTab: TabKey) => {
    setTab(nextTab);

    if (nextTab === "incoming") {
      refetchIncoming();
    } else if (nextTab === "matches") {
      refetchMatches();
    } else {
      refetchOutgoing();
    }
  };

  const closeProfile = () => setSelectedProfile(null);
  const closeConfirm = () => {
    if (!isBusy) {
      setConfirmAction(null);
    }
  };

  const requestReject = (id: number, profileName?: string) => {
    setConfirmAction({
      id,
      type: "reject",
      title: "Отклонить заявку?",
      description: profileName
        ? `Заявка от ${profileName} будет отклонена.`
        : "Заявка будет отклонена.",
      confirmText: "Отклонить",
    });
  };

  const requestCancel = (id: number, profileName?: string) => {
    setConfirmAction({
      id,
      type: "cancel",
      title: "Отменить?",
      description: profileName
        ? `Связь с ${profileName} будет отменена.`
        : "Это действие будет отменено.",
      confirmText: "Отменить",
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      if (confirmAction.type === "reject") {
        await reject(confirmAction.id).unwrap();
      } else {
        await cancel(confirmAction.id).unwrap();
      }

      refreshMyTabs();
      setConfirmAction(null);
    } catch {
      // Ошибка останется в состоянии RTK Query; модалку не закрываем, чтобы пользователь мог повторить.
    }
  };

  const renderGrid = (items: any[], renderItem: (item: any) => React.ReactNode, loading: boolean) => {
    if (loading) return <Loader />;
    if (!items || items.length === 0) {
      return <div className={styles.empty}>Пусто</div>;
    }
    return <div className={styles.grid}>{items.map(renderItem)}</div>;
  };

  const getTabLabel = (tabKey: TabKey, label: string) => {
    const count = tabKey === "incoming"
      ? unreadCounts?.incoming ?? 0
      : tabKey === "matches"
        ? unreadCounts?.matches ?? 0
        : 0;

    return count > 0 ? `${label} (+${count})` : label;
  };

  return (
    <>
      <Heading tpl="new">Свои</Heading>
      <div className={styles.wrapper}>
        <div className={styles.tabs}>
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`${styles.tab} ${tab === t.key ? styles.active : ""}`}
              onClick={() => handleTabClick(t.key)}
            >
              {getTabLabel(t.key, t.label)}
            </button>
          ))}
        </div>

        {tab === "incoming" &&
          renderGrid(
            incoming || [],
            (req: TMatchRequest) => (
              <Card key={req.id} profile={req.sender_profile} onInfoClick={setSelectedProfile}>
                <button
                  disabled={isBusy}
                  className={styles.secondary}
                  onClick={() => requestReject(req.id, req.sender_profile?.name)}
                  aria-label="Отклонить заявку"
                >
                  <CrossIcon />
                </button>
                <button disabled={isBusy} className={styles.primary} onClick={() => handleAccept(req.id)}>
                  Свой!
                </button>
              </Card>
            ),
            loadingIncoming
          )}

        {tab === "matches" &&
          renderGrid(
            matches || [],
            (item: TMatchItem) => (
              <Card key={item.match_request.id} profile={item.partner_profile} onInfoClick={setSelectedProfile}>
                <button
                  disabled={isBusy}
                  className={styles.cancelButton}
                  onClick={() => requestCancel(item.match_request.id, item.partner_profile?.name)}
                  aria-label="Отменить матч"
                >
                  Отменить
                </button>
              </Card>
            ),
            loadingMatches
          )}

        {tab === "outgoing" &&
          renderGrid(
            outgoing || [],
            (req: TMatchRequest) => (
              <Card key={req.id} profile={req.receiver_profile} onInfoClick={setSelectedProfile}>
                <button
                  disabled={isBusy}
                  className={styles.cancelButton}
                  onClick={() => requestCancel(req.id, req.receiver_profile?.name)}
                  aria-label="Отменить заявку"
                >
                  Отменить
                </button>
              </Card>
            ),
            loadingOutgoing
          )}

        <ProfilePreviewModal
          profile={selectedProfile}
          isOpen={Boolean(selectedProfile)}
          onClose={closeProfile}
        />

        <ConfirmModal
          isOpen={Boolean(confirmAction)}
          title={confirmAction?.title ?? ""}
          description={confirmAction?.description}
          confirmText={confirmAction?.confirmText}
          cancelText="Назад"
          loadingText="Отмена..."
          isLoading={isRejecting || isCanceling}
          onCancel={closeConfirm}
          onConfirm={handleConfirmAction}
        />
      </div>
    </>
  );
}

export default MyPage;
